'use client';

import { type TimelineSummaryTotals, TimelineSummaryTotalsSchema } from '@sweetistics/contracts/timeline-insights';
import { createWithEqualityFn } from 'zustand/traditional';

import type { TimelineTopicWithRoute } from '@/domains/timeline/components/analysis/TimelineAnalysisInsights';
import type { TimeRange } from '@/types/timeline-analysis';
import type { UserListCollections } from '@/types/user-profile';

export interface TimelineTopicFilter {
  id: string;
  keywords?: string[] | undefined;
  label: string;
  sentiment?: 'positive' | 'neutral' | 'negative' | undefined;
  slug?: string | undefined;
  summary?: string | undefined;
  tweetIds: string[];
}

export interface TimelineListFilter {
  description?: string | undefined;
  id: string;
  name: string;
  slug?: string | null | undefined;
}

export type TimelineContextValue = Pick<
  TimelineStoreState,
  | 'timeRange'
  | 'setTimeRange'
  | 'isRefreshing'
  | 'setIsRefreshing'
  | 'handleRefresh'
  | 'setHandleRefresh'
  | 'handleAnalysisRequest'
  | 'setHandleAnalysisRequest'
  | 'registerAnalysisHandler'
  | 'topicFilter'
  | 'setTopicFilter'
  | 'clearTopicFilter'
  | 'listFilter'
  | 'setListFilter'
  | 'clearListFilter'
  | 'lists'
  | 'setLists'
  | 'topics'
  | 'setTopics'
  | 'timelineTotals'
  | 'setTimelineTotals'
>;

const DEFAULT_TIME_RANGE: TimeRange = '24h';

const cloneListCollection = (lists: UserListCollections): UserListCollections => ({
  owned: lists.owned.map((entry) => ({ ...entry })),
  member: lists.member.map((entry) => ({ ...entry })),
});

const listsAreEqual = (next: UserListCollections, previous: UserListCollections | null): boolean => {
  if (!previous) {
    return false;
  }
  if (previous.owned.length !== next.owned.length || previous.member.length !== next.member.length) {
    return false;
  }
  for (let index = 0; index < next.owned.length; index += 1) {
    if (next.owned[index]?.id !== previous.owned[index]?.id) {
      return false;
    }
  }
  for (let index = 0; index < next.member.length; index += 1) {
    if (next.member[index]?.id !== previous.member[index]?.id) {
      return false;
    }
  }
  return true;
};

type TimelineStoreBaseState = {
  timeRange: TimeRange;
  isRefreshing: boolean;
  topicFilter: TimelineTopicFilter | null;
  listFilter: TimelineListFilter | null;
  lists: UserListCollections | null;
  topics: TimelineTopicWithRoute[];
  timelineTotals: TimelineSummaryTotals | null;
  refreshHandler: (() => void) | null;
  analysisHandler: ((forceRefresh?: boolean) => void) | null;
};

type TimelineStoreActions = {
  setTimeRange: (range: TimeRange) => void;
  setIsRefreshing: (refreshing: boolean) => void;
  setTopicFilter: (filter: TimelineTopicFilter | null) => void;
  clearTopicFilter: () => void;
  setListFilter: (filter: TimelineListFilter | null) => void;
  clearListFilter: () => void;
  setLists: (lists: UserListCollections | null) => void;
  setTopics: (topics: TimelineTopicWithRoute[]) => void;
  setTimelineTotals: (totals: TimelineSummaryTotals | null) => void;
  setHandleRefresh: (handler: (() => void) | undefined) => void;
  handleRefresh: (forceRefresh?: boolean) => void;
  setHandleAnalysisRequest: (handler: ((forceRefresh?: boolean) => void) | undefined) => void;
  handleAnalysisRequest: (forceRefresh?: boolean) => void;
  registerAnalysisHandler: (handler: (forceRefresh?: boolean) => void) => () => void;
  reset: () => void;
  markActive: (value: boolean) => void;
};

export interface TimelineStoreState extends TimelineStoreBaseState, TimelineStoreActions {
  isActive: boolean;
}

const initialState: TimelineStoreBaseState & { isActive: boolean } = {
  timeRange: DEFAULT_TIME_RANGE,
  isRefreshing: false,
  topicFilter: null,
  listFilter: null,
  lists: null,
  topics: [],
  timelineTotals: null,
  refreshHandler: null,
  analysisHandler: null,
  isActive: false,
};

export const useTimelineStore = createWithEqualityFn<TimelineStoreState>()((set, get) => ({
  ...initialState,
  setTimeRange: (range) => {
    set({ timeRange: range });
  },
  setIsRefreshing: (refreshing) => {
    set({ isRefreshing: refreshing });
  },
  setTopicFilter: (filter) => {
    set({ topicFilter: filter });
  },
  clearTopicFilter: () => {
    set({ topicFilter: null });
  },
  setListFilter: (filter) => {
    set({ listFilter: filter });
  },
  clearListFilter: () => {
    set({ listFilter: null });
  },
  setLists: (lists) => {
    if (!lists || (lists.owned.length === 0 && lists.member.length === 0)) {
      set({ lists: null });
      return;
    }
    const nextSnapshot = cloneListCollection(lists);
    // Preserve reference equality when the caller replays the same list ordering so memoised consumers stay asleep.
    set((state) => (listsAreEqual(nextSnapshot, state.lists) ? state : { lists: nextSnapshot }));
  },
  setTopics: (topics) => {
    set({ topics: [...topics] });
  },
  setTimelineTotals: (totals) => {
    const normalized = totals ? TimelineSummaryTotalsSchema.parse(totals) : null;
    if (!normalized) {
      set({ timelineTotals: null });
      return;
    }
    const snapshot: TimelineSummaryTotals = {
      ...normalized,
      filterCounts: normalized.filterCounts.map((entry) => ({ ...entry })),
    };
    // We compare the incoming snapshot so we only wake subscribers when something meaningful changed.
    set((state) => {
      if (!state.timelineTotals) {
        return { timelineTotals: snapshot };
      }
      const current = state.timelineTotals;
      const metaStable =
        current.totalPosts === snapshot.totalPosts &&
        current.links === snapshot.links &&
        current.replies === snapshot.replies &&
        current.quotes === snapshot.quotes &&
        current.original === snapshot.original &&
        current.retweets === snapshot.retweets;
      const filterCountsStable =
        current.filterCounts.length === snapshot.filterCounts.length &&
        current.filterCounts.every((entry, index) => {
          const next = snapshot.filterCounts[index];
          return (
            !!next &&
            entry.isRetweet === next.isRetweet &&
            entry.isReply === next.isReply &&
            entry.hasLinks === next.hasLinks &&
            entry.hasMedia === next.hasMedia &&
            entry.count === next.count
          );
        });
      if (metaStable && filterCountsStable) {
        return state;
      }
      return { timelineTotals: snapshot };
    });
  },
  setHandleRefresh: (handler) => {
    set({ refreshHandler: handler ?? null });
  },
  handleRefresh: () => {
    const handler = get().refreshHandler;
    handler?.();
  },
  setHandleAnalysisRequest: (handler) => {
    set({ analysisHandler: handler ?? null });
  },
  handleAnalysisRequest: (forceRefresh) => {
    const handler = get().analysisHandler;
    handler?.(forceRefresh);
  },
  registerAnalysisHandler: (handler) => {
    set({ analysisHandler: handler });
    return () => {
      if (get().analysisHandler === handler) {
        set({ analysisHandler: null });
      }
    };
  },
  reset: () => {
    set((state) => ({
      ...state,
      timeRange: DEFAULT_TIME_RANGE,
      isRefreshing: false,
      topicFilter: null,
      listFilter: null,
      lists: null,
      topics: [],
      timelineTotals: null,
      refreshHandler: null,
      analysisHandler: null,
    }));
  },
  markActive: (value) => {
    set({ isActive: value });
  },
}));

export const TIMELINE_DEFAULT_TIME_RANGE = DEFAULT_TIME_RANGE;
